import React, { useEffect } from 'react';
import { Card, Typography, Row, Col, Button } from 'antd';
import { useHistory } from 'react-router-dom';
import { ShoppingCartOutlined, SunOutlined, HomeOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const LosAngeles = () => {
  const history = useHistory();

  useEffect(() => {
    const localSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage", 
      "name": "Comfort Bears in Los Angeles - Lumi Bears",
      "description": "Premium comfort companions for Los Angeles families. Emotional support bears designed to help children in LA with bedtime routines and emotional regulation.",
      "url": "https://lumibears.com/locations/los-angeles",
      "about": {
        "@type": "Product",
        "name": "Lumi Comfort Bears",
        "description": "Emotional support companions for children"
      },
      "audience": {
        "@type": "Audience",
        "geographicArea": {
          "@type": "City", 
          "name": "Los Angeles",
          "containedInPlace": {
            "@type": "State",
            "name": "California"
          }
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(localSchema);
    document.head.appendChild(script);

    document.title = "Comfort Bears Los Angeles - Emotional Support for LA Children | Lumi Bears";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium comfort bears for Los Angeles families. Help LA children with bedtime anxiety and emotional support. Fast shipping to Hollywood, Santa Monica, Venice.');
    }

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent.includes('los-angeles')) {
        existingScript.remove();
      }
      document.title = "Lumi Bears - Comfort Companions for Sensitive Children | Emotional Support Teddy Bears";
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Premium comfort companions for children who need extra emotional support. Soft, intelligent teddy bears designed to help kids feel calm and secure during bedtime and challenging moments.');
      }
    };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Hero Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '60px 0',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Row gutter={[48, 32]} align="middle">
            <Col xs={24} lg={14}>
              <Title level={1} style={{ color: 'white', marginBottom: '20px', fontSize: '2.5rem' }}>
                Comfort Bears in Los Angeles
              </Title>
              <Paragraph style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', marginBottom: '30px' }}>
                Supporting LA families with premium comfort companions designed for children who need 
                extra emotional support. From Hollywood to Santa Monica, Venice to Pasadena - we're here 
                to help your child thrive in the City of Angels.
              </Paragraph>
              <Button 
                type="primary" 
                size="large" 
                icon={<ShoppingCartOutlined />}
                onClick={() => history.push('/')}
                style={{ 
                  background: 'white', 
                  borderColor: 'white', 
                  color: '#667eea',
                  height: '50px',
                  fontSize: '16px'
                }}
              >
                Shop Comfort Bears
              </Button>
            </Col>
            <Col xs={24} lg={10}>
              <div style={{ textAlign: 'center' }}>
                <SunOutlined style={{ fontSize: '120px', color: 'rgba(255,255,255,0.3)' }} />
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 20px' }}>
        
        {/* Why LA Families Choose Lumi Bears */}
        <Row gutter={[48, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} lg={16}>
            <Title level={2} style={{ color: '#333', marginBottom: '24px' }}>
              Why Los Angeles Families Trust Lumi Bears
            </Title>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.7', color: '#666', marginBottom: '20px' }}>
              Los Angeles life brings unique opportunities and challenges for families. From the entertainment industry's 
              pressures to the diversity of school environments, LA parents need comfort solutions that help children 
              navigate complex social and emotional landscapes while staying grounded and confident.
            </Paragraph>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.7', color: '#666' }}>
              Whether you're in Beverly Hills, downtown LA, or the beach communities, our comfort bears provide 
              consistent emotional support that adapts to your family's lifestyle - from auditions to playdates, 
              busy schedules to quiet moments at home.
            </Paragraph>
          </Col>
          <Col xs={24} lg={8}>
            <Card style={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <HomeOutlined style={{ fontSize: '48px', color: '#667eea', marginBottom: '16px' }} />
              <Title level={4} style={{ color: '#333' }}>California Lifestyle Ready</Title>
              <Text style={{ color: '#666' }}>
                Built for active LA families who need emotional support tools that keep up with their dynamic lifestyle.
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Shipping & Delivery Info */}
        <Card style={{ marginBottom: '40px', background: '#fff8f0' }}>
          <Row gutter={[32, 24]} align="middle">
            <Col xs={24} md={4} style={{ textAlign: 'center' }}>
              <ClockCircleOutlined style={{ fontSize: '48px', color: '#667eea' }} />
            </Col>
            <Col xs={24} md={20}>
              <Title level={3} style={{ color: '#333', marginBottom: '16px' }}>
                Fast Delivery to Los Angeles Area
              </Title>
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>Central LA & Hollywood:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>West Side & Beaches:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>Valley & Pasadena:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
              </Row>
              <Paragraph style={{ marginTop: '16px', color: '#666', marginBottom: 0 }}>
                <Text italic>
                  All orders ship with tracking. Current delivery time reflects our pre-order production schedule 
                  as we carefully craft each comfort companion for California families.
                </Text>
              </Paragraph>
            </Col>
          </Row>
        </Card>

        {/* LA-Specific Challenges */}
        <Title level={2} style={{ color: '#333', marginBottom: '30px', textAlign: 'center' }}>
          Supporting LA Children Through Unique Challenges
        </Title>
        
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Performance Pressure</Title>
              <Paragraph style={{ color: '#666' }}>
                From school presentations to potential entertainment opportunities, LA children often face performance 
                pressures. Comfort bears provide consistent emotional support before auditions, recitals, and other high-stress situations.
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Diverse Social Environments</Title>
              <Paragraph style={{ color: '#666' }}>
                LA's incredible diversity is a strength, but navigating different social groups and cultural expectations 
                can be challenging for children. Comfort bears offer stable emotional anchoring across all social settings.
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Long Commutes & Traffic</Title>
              <Paragraph style={{ color: '#666' }}>
                LA traffic means children spend significant time in cars traveling between activities. Portable comfort 
                bears provide emotional regulation support during long commutes and transitions between locations.
              </Paragraph>
            </Card>
          </Col>
        </Row>

        {/* Family Resources for LA */}
        <Card style={{ marginBottom: '40px' }}>
          <Title level={3} style={{ color: '#333', marginBottom: '24px' }}>
            Resources for Los Angeles Families
          </Title>
          <Row gutter={[32, 24]}>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#667eea' }}>Child Development Support</Title>
              <ul style={{ color: '#666', lineHeight: '1.6' }}>
                <li>
                  <a href="https://www.chla.org/behavioral-health" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                    Children's Hospital Los Angeles - Behavioral Health
                  </a>
                </li>
                <li>
                  <a href="https://achieve.lausd.net/mentalhealth" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                    LAUSD Mental Health Resources
                  </a>
                </li>
              </ul>
            </Col>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#667eea' }}>Family Activities & Wellness</Title>
              <ul style={{ color: '#666', lineHeight: '1.6' }}>
                <li>
                  <a href="https://www.laparks.org/recreation-centers" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                    LA Parks & Recreation - Family Programs
                  </a>
                </li>
                <li>
                  <a href="https://www.lapl.org/kids" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                    Los Angeles Public Library - Children's Programs
                  </a>
                </li>
              </ul>
            </Col>
          </Row>
        </Card>

        {/* Neighborhoods We Serve */}
        <Card style={{ marginBottom: '40px', background: '#f5f5f5' }}>
          <Title level={3} style={{ color: '#333', marginBottom: '24px' }}>
            Serving LA Neighborhoods & Communities
          </Title>
          <Row gutter={[24, 16]}>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#667eea' }}>Central LA:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Hollywood</li>
                <li>Downtown LA</li>
                <li>Silver Lake</li>
                <li>Los Feliz</li>
              </ul>
            </Col>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#667eea' }}>West Side:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Santa Monica</li>
                <li>Venice</li>
                <li>Beverly Hills</li>
                <li>West Hollywood</li>
              </ul>
            </Col>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#667eea' }}>Valley:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Studio City</li>
                <li>Sherman Oaks</li>
                <li>Encino</li>
                <li>Burbank</li>
              </ul>
            </Col>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#667eea' }}>East & South:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Pasadena</li>
                <li>Culver City</li>
                <li>Manhattan Beach</li>
                <li>Long Beach</li>
              </ul>
            </Col>
          </Row>
        </Card>

        {/* Call to Action */}
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
          borderRadius: '12px'
        }}>
          <Title level={2} style={{ color: '#4a148c', marginBottom: '20px' }}>
            Ready to Support Your LA Child?
          </Title>
          <Paragraph style={{ fontSize: '18px', color: '#6a1b9a', marginBottom: '30px', maxWidth: '600px', margin: '0 auto 30px auto' }}>
            Join Los Angeles families who are giving their children the emotional support tools they need to thrive in the entertainment capital of the world.
          </Paragraph>
          <Button 
            type="primary" 
            size="large" 
            icon={<ShoppingCartOutlined />}
            onClick={() => history.push('/')}
            style={{ 
              background: '#4a148c', 
              borderColor: '#4a148c',
              height: '50px',
              fontSize: '18px',
              padding: '0 30px'
            }}
          >
            Shop Comfort Bears
          </Button>
        </div>

      </div>
    </div>
  );
};

export default LosAngeles;